import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BehaviorSubject, firstValueFrom, Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ListComponent } from "~app/common/components/list/list.component";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { Planeringsstatus, RaskRoller, SkogsvardAtgardsGrupp, SkogsvardsStatus } from "~app/common/models/constants";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FeatureService } from "~app/common/services/feature.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { canEdit, updatePermissions } from "~app/role-permissions";
import { UpdateAtgardCommand } from "../../commands/atgard/UpdateAtgardCommand";
import { ATGARD_PLANERINGSSTATUS } from "../../models/atgard-models";
import { IVKAtgardKedjaKopplingMasshanteringsResponseDTO } from "../../models/Atgardskedjor/atgardskedjor-models";
import { SKOGSVARD_PLANERINGSSTATUS } from "../../models/skogsvard/skogsvard-models";
import { AtgardKedjaMediatorService } from "../../services/atgardkedja-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { AtgardKedjaValidator } from "./atgard-kedja.validation";
@Component({
    selector: "rask-atgard-kedja-hanterings-dialog",
    templateUrl: "./atgard-kedja-hanterings-dialog.component.html",
    styleUrl: "./atgard-kedja-hanterings-dialog.component.scss",
    imports: [RaskCommonModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        updatePermissions({
            role: RaskRoller.role.Kopplanera,
            canEdit: false
        })
    ]
})
export class AtgardKedjaHanteringsDialogComponent implements OnInit, OnDestroy {

    @ViewChild("atgardKedjeList") atgardKedjeListInstance: ListComponent;
    modal = inject(NgbActiveModal);
    atgardPlaneringsstatusLista: { text: string; value: string; disabled: boolean }[];
    skogsvardAtgardPlaneringsstatusLista: { text: string; value: string; disabled: boolean }[];
    protected readonly messages$ = new BehaviorSubject<IMessageModel[]>([]);

    protected kedjaKopplingar: IVKAtgardKedjaKopplingMasshanteringsResponseDTO[];
    protected kedjeGuid: string;
    protected shortDate = DateUtils.shortDateString;
    protected oppnatFranKontraktId: number;
    protected kontraktReadonly: boolean;

    protected messageItemsAsObservable: Observable<IMessageModel[]> = this.messages$.asObservable();
    protected isDirty = false;
    protected relativaArFeatureActive: boolean;
    protected minSelectableDate = new Date(new Date().setDate(2));

    private readonly atgardKedjaMediatorService = inject(AtgardKedjaMediatorService);
    private readonly featureService = inject(FeatureService);
    private readonly busyService = inject(BusyService);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly commandService = inject(CommandService);
    private kedjaValidator: AtgardKedjaValidator;
    private readonly canEdit = canEdit();

    init(kedjeGuid: string, kontraktId: number) {
        this.kedjeGuid = kedjeGuid;
        this.oppnatFranKontraktId = kontraktId;
        const isDisabled = (status: string) =>
            status === Planeringsstatus.Tillgangligt
            && !this.canEdit();

        this.atgardPlaneringsstatusLista = ATGARD_PLANERINGSSTATUS.map((value) => ({
            text: value,
            value,
            disabled: isDisabled(value)
        }));

        this.skogsvardAtgardPlaneringsstatusLista = SKOGSVARD_PLANERINGSSTATUS.map((value) => ({
            text: value,
            value,
            disabled: isDisabled(value)
        }));
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.kedjaValidator = new AtgardKedjaValidator();
        this.loadData();
        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(readonly => this.kontraktReadonly = readonly);

        firstValueFrom(this.featureService.getFeatureActive("RelativaAr"))
            .then(active => this.relativaArFeatureActive = active);
    }

    protected getSelectlistItems(item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) {
        return item.isSkogsvard ? this.skogsvardAtgardPlaneringsstatusLista : this.atgardPlaneringsstatusLista;
    }

    protected isSelectorDisabled(item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO): boolean {
        if (this.kontraktReadonly || this.isAtgardFranAnnatKontrakt(item)) {
            return true;
        }
        if(item.isSkogsvard && item.skogsvardStatus) {
            return !SkogsvardsStatus.innanTillganglig.includes(item.skogsvardStatus) || item.status === SkogsvardsStatus.tillganglig;
        }
        return item.isSkogsvard ? ObjectUtils.hasValue(item.slutjusteratDatum) : item.atgardUtford;
    }

    protected onPlaneringsstatusChange(item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO, value: string): void {
        this.isDirty = true;
        const atgard = this.kontraktMediatorService.atgarder.find(x => x.id === item.atgardId);
        atgard.status = value;
        const command = new UpdateAtgardCommand(atgard, this.kontraktMediatorService.kontrakt.guid);
        this.commandService.addTransactionCommand(command);
    }

    protected isAnyHuvudatgardChecked(item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) {
        return this.kedjaKopplingar.some(x => x.huvudatgard && x.atgardGuid !== item.atgardGuid) || (item.huvudatgard && item.atgardUtford);
    }

    protected isBeforeHuvudatgard = (item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) => {
        const huvudatgardIndex = this.kedjaKopplingar.findIndex(x => x.huvudatgard === true);
        const rowIndex = this.kedjaKopplingar.findIndex(x => x.atgardGuid === item.atgardGuid);
        return rowIndex < huvudatgardIndex;
    };

    protected isSkogsvardsAtgard = (item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) => SkogsvardAtgardsGrupp.Alla.includes(item.atgardsgrupp);

    protected isRelativaArDisabled(item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) {
        return this.isBeforeHuvudatgard(item) || this.isHuvudatgardUtford() || this.isAtgardFranAnnatKontrakt(item);
    }
    protected isOnskatDatumDisabled(item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) {
        if (this.kontraktReadonly || this.isAtgardFranAnnatKontrakt(item)) {
            return true;
        }

        return item.status === Planeringsstatus.Tillgangligt || item.slutjusteratDatum != null;
    }

    protected isAtgardFranAnnatKontrakt(item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO): boolean {
        return this.oppnatFranKontraktId !== item.kontraktId;
    }

    protected isHuvudatgardUtford(): boolean {
        const huvudatgard = this.kedjaKopplingar.find(x => x.huvudatgard);
        return huvudatgard?.atgardUtford;
    }

    protected setHuvudatgard = (item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) => {
        this.isDirty = true;
        const isAlreadyHuvudatgard = this.kedjaKopplingar.find(k => k.atgardGuid === item.atgardGuid)?.huvudatgard;
        this.kedjaKopplingar.forEach(k => k.huvudatgard = !isAlreadyHuvudatgard && k.atgardGuid === item.atgardGuid);
        if (!isAlreadyHuvudatgard) {
            this.clearRelativaArUpToAndIncluding(item);
        }
        this.cdRef.detectChanges();
    };

    protected onRelativaArChanged = (item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) => {
        this.isDirty = true;
    };

    protected onOnskatDatumChanged = (item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) => {
        this.isDirty = true;
        const atgard = this.kontraktMediatorService.atgarder.find(x => x.id === item.atgardId);
        atgard.onskatDatum = item.onskatDatum;
        const command = new UpdateAtgardCommand(atgard, this.kontraktMediatorService.kontrakt.guid);
        this.commandService.addTransactionCommand(command);
    };

    protected dismiss(): void {
        this.commandService.reset();
        this.modal.close();
    }

    protected moveInList(item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO, relativeIndex: number) {
        this.isDirty = true;
        const reorderedKopplingar = [...this.kedjaKopplingar];

        // Hämta ut positionen för kopplingen i kedjan
        const currentIndex = reorderedKopplingar.indexOf(item);

        // Ta bort kopplingen från kedjan
        reorderedKopplingar.splice(currentIndex, 1);

        // Stoppar in kopplingen på rätt position
        reorderedKopplingar.splice((currentIndex + relativeIndex), 0, item);

        // Omladdning etc.
        this.kedjaKopplingar = reorderedKopplingar;
        this.isValid(false);
        this.cdRef.detectChanges();
    }

    protected save(): void {
        if (this.isValid(true)) {
            this.busyService.addWork(
                this.atgardKedjaMediatorService.saveMassEditeringsKedja(this.kedjaKopplingar)
            ).subscribe(() => {
                this.isDirty = false;
                this.dismiss();
            });
        }
    }

    private clearRelativaArUpToAndIncluding(item: IVKAtgardKedjaKopplingMasshanteringsResponseDTO) {
        const currentIndex = this.kedjaKopplingar.indexOf(item);
        for (let i = 0; i <= currentIndex; i++) {
            this.kedjaKopplingar[i].relativaAr = null;
        }
    }

    private isValid(isError: boolean): boolean {
        const serverity = isError ? MessageSeverity.Error : MessageSeverity.Info;

        this.kedjaValidator.validate(this.kedjaKopplingar);

        const validateMessages = this.kedjaValidator.validateMessages.map(msg => ({
            message: msg,
            anchor: "",
            severity: serverity
        }));

        this.messages$.next(validateMessages);
        return validateMessages.length === 0;
    }

    private loadData(): void {
        this.busyService.addWork(this.atgardKedjaMediatorService.getMasshanteraKedjaKopplingarByKedjaGuid(this.kedjeGuid))
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(kedjaKopplingar => {
                this.kedjaKopplingar = kedjaKopplingar;
            });
    }
}
